import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { shareSchema, wsChannel } from '@xdocs/schemas';
import type { ErrorAlert, Share, ShareRole } from '@xdocs/schemas';
import { useWs } from '../../ws/WsProvider.js';

export interface UseShareResult {
  /** The document's PRIVATE/PUBLIC/LINK primary share row — `null` while
   * still private and never published this session. */
  primary: Share | null;
  /** `visibility='user'` grant rows for this document, session-known only
   * (see this file's doc comment — OPEN ISSUE: no list-by-document op). */
  grants: readonly Share[];
  isPending: boolean;
  error: ErrorAlert | null;
  publishPublic: () => Promise<Share>;
  publishLink: (linkExpiresAt?: Date) => Promise<Share>;
  makePrivate: () => Promise<Share>;
  revokeLink: () => Promise<Share>;
  setLinkExpiry: (linkExpiresAt: Date | null) => Promise<Share>;
  addGrant: (userId: string, role: ShareRole) => Promise<Share>;
  removeGrant: (shareId: string) => Promise<boolean>;
}

/**
 * Bundles the F27/F62/F63/F64 share-management ops for ONE document behind
 * a single stateful hook so `ShareDialog` stays a render-only component.
 *
 * OPEN ISSUE (returned in this task's issues list): the frozen M3
 * `op-contracts.ts` has no "get the current share state for a documentId"
 * or "list grants for a documentId" op — `share.get`/`share.list` are keyed
 * by `shareId`/the caller's OWN full share list, neither indexed by
 * `documentId`. So a dialog opened fresh from a document (editor/explorer/
 * personas, no known `shareId` yet) genuinely cannot discover a
 * PREVIOUSLY-published visibility or PREVIOUSLY-granted users on cold
 * load — only what `initialShare`/`initialGrants` are seeded with by a
 * caller that already has them (e.g. `/shares` row click) or what THIS
 * hook itself creates/updates during its own session. A proper fix needs a
 * `share.getByDocument`/`share.grants` op from the schema/server owner.
 */
export function useShare(documentId: string, initialShare: Share | null = null, initialGrants: readonly Share[] = []): UseShareResult {
  const { wsClient } = useWs();
  const queryClient = useQueryClient();
  const [primary, setPrimary] = useState<Share | null>(initialShare);
  const [grants, setGrants] = useState<readonly Share[]>(initialGrants);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<ErrorAlert | null>(null);

  const invalidate = useCallback(
    (share: Share) => {
      void queryClient.invalidateQueries({ queryKey: ['shares', 'list'] });
      void queryClient.invalidateQueries({ queryKey: ['share', share.id] });
      void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
    },
    [queryClient, documentId],
  );

  // Live moderation verdict (F27/F74): the moderate-worker pushes on
  // `share:{shareId}` when the classify verdict lands (`moderation_verdict`)
  // — without this subscription the open dialog stayed frozen on
  // "Waiting for moderation…" forever (the "Live on Discover" state was
  // unreachable without a manual reload). Re-fetch the share row on any
  // push for it; `shareSchema.parse` (not the raw payload) because
  // `requestTyped` is compile-time-only and the row's timestamps arrive as
  // ISO strings otherwise.
  const primaryId = primary?.id ?? null;
  useEffect(() => {
    if (!primaryId) return undefined;
    return wsClient.subscribe(wsChannel.share(primaryId), () => {
      requestTyped(wsClient, 'share.get', { shareId: primaryId })
        .then((share) => setPrimary(shareSchema.parse(share)))
        .catch(() => {
          // Best-effort live refresh — the dialog still shows the last-known
          // state, and any subsequent user action re-syncs via its own op.
        });
    });
  }, [wsClient, primaryId]);

  async function run<T>(fn: () => Promise<T>): Promise<T> {
    setIsPending(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(err as ErrorAlert);
      throw err;
    } finally {
      setIsPending(false);
    }
  }

  const publishPublic = useCallback(
    () =>
      run(async () => {
        const share = await requestTyped(wsClient, 'share.publish', { documentId, visibility: 'public' });
        setPrimary(share);
        invalidate(share);
        return share;
      }),
    [wsClient, documentId, invalidate],
  );

  const publishLink = useCallback(
    (linkExpiresAt?: Date) =>
      run(async () => {
        const share = await requestTyped(wsClient, 'link.issue', { documentId, ...(linkExpiresAt ? { linkExpiresAt } : {}) });
        setPrimary(share);
        invalidate(share);
        return share;
      }),
    [wsClient, documentId, invalidate],
  );

  const makePrivate = useCallback(
    () =>
      run(async () => {
        if (!primary) {
          const share = await requestTyped(wsClient, 'share.publish', { documentId, visibility: 'private' });
          setPrimary(share);
          invalidate(share);
          return share;
        }
        const share = await requestTyped(wsClient, 'share.revoke', { shareId: primary.id });
        setPrimary(share);
        invalidate(share);
        return share;
      }),
    [wsClient, documentId, primary, invalidate],
  );

  const revokeLink = useCallback(
    () =>
      run(async () => {
        if (!primary) throw new Error('no link to revoke');
        const share = await requestTyped(wsClient, 'link.revoke', { shareId: primary.id });
        setPrimary(share);
        invalidate(share);
        return share;
      }),
    [wsClient, primary, invalidate],
  );

  const setLinkExpiry = useCallback(
    (linkExpiresAt: Date | null) =>
      run(async () => {
        if (!primary) throw new Error('no link to update');
        const share = await requestTyped(wsClient, 'share.update', { shareId: primary.id, linkExpiresAt });
        setPrimary(share);
        invalidate(share);
        return share;
      }),
    [wsClient, primary, invalidate],
  );

  const addGrant = useCallback(
    (userId: string, role: ShareRole) =>
      run(async () => {
        const share = await requestTyped(wsClient, 'share.grant', { documentId, userId, role });
        setGrants((prev) => [...prev.filter((g) => g.id !== share.id), share]);
        invalidate(share);
        return share;
      }),
    [wsClient, documentId, invalidate],
  );

  const removeGrant = useCallback(
    (shareId: string) =>
      run(async () => {
        const result = await requestTyped(wsClient, 'share.ungrant', { shareId });
        if (result.revoked) setGrants((prev) => prev.filter((g) => g.id !== shareId));
        void queryClient.invalidateQueries({ queryKey: ['shares', 'list'] });
        void queryClient.invalidateQueries({ queryKey: ['document', documentId] });
        return result.revoked;
      }),
    [wsClient, documentId, queryClient],
  );

  return { primary, grants, isPending, error, publishPublic, publishLink, makePrivate, revokeLink, setLinkExpiry, addGrant, removeGrant };
}
